const MXN = new Intl.NumberFormat('es-MX', {
  style: 'currency',
  currency: 'MXN',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const INT = new Intl.NumberFormat('es-MX', { maximumFractionDigits: 0 });

// Pin the timezone so the freshness footer reads correctly regardless of the
// dev/CI machine's local TZ. America/Mexico_City is permanent UTC-6 (Mexico
// suspended DST for most of the country in 2022); customers all live there.
const HOUR = new Intl.DateTimeFormat('es-MX', {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
  timeZone: 'America/Mexico_City',
});

export function formatMxn(value: number): string {
  // Intl returns "MX$184,350.42" by default; collapse to "$184,350.42" for visual clarity.
  return MXN.format(value).replace('MX$', '$');
}

/** Compact MXN for tight cells (KPI sub-lines, aging breakdowns):
 *  712_300 -> "$712K", 1_204_889 -> "$1.2M", 450 -> "$450". */
export function formatCompactMxn(value: number): string {
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}$${Math.round(abs / 1_000)}K`;
  return `${sign}$${Math.round(abs)}`;
}

export function formatInt(value: number): string {
  return INT.format(value);
}

export function formatPctDelta(current: number, previous: number): string {
  if (previous === 0) return '—';
  const pct = ((current - previous) / previous) * 100;
  const sign = pct >= 0 ? '+' : '';
  return `${sign}${pct.toFixed(1)}%`;
}

export function formatHourLabel(hh: string): string {
  return `${hh}h`;
}

export function formatExportedAt(iso: string): string {
  return HOUR.format(new Date(iso));
}

/**
 * Local-date YYYY-MM-DD. We CAN'T use `d.toISOString().slice(0, 10)` because
 * `toISOString()` is always UTC — for a Mexican Pacesetter user clicking a
 * dashboard at 7pm CST, UTC has already rolled past midnight and the SPA
 * would query "tomorrow", returning zero tickets.
 */
export function localYMD(d: Date = new Date()): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

import { t } from './i18n';

/**
 * Pretty-print a daemon column key. Returns the friendly Spanish label from
 * t.fields if known; otherwise echoes the raw key so unknown columns still
 * render (just without the polish). Adding a new daemon column without
 * updating t.fields just loses i18n for that one column, not function.
 */
export function fieldLabel(key: string): string {
  return t.fields[key] ?? key;
}

/**
 * Build a CSV string from already-rendered JSON rows + an ordered column
 * list. The header row uses friendly labels so accountants opening the CSV
 * in Excel get "Subtotal MXN" instead of "subt_mxn". Cell values are coerced
 * via the same logic as the on-screen table.
 *
 * Lives here (not in api/reports.ts) so any future "download CSV" surface —
 * not just the report builder — can share the same friendly-header path.
 */
/**
 * Render an ISO date string ("2026-05-31") as a Spanish relative phrase
 * anchored at "now". Used on the dashboard's last_sold column so a glance
 * tells the operator "is this part actually idle?" rather than asking them
 * to mental-subtract two dates.
 *
 * Falls back to the raw ISO when input is empty/null so we never lose
 * information.
 */
export function formatRelativeDay(iso: string | null | undefined): string {
  if (!iso) return '—';
  // Parse YYYY-MM-DD as local midnight so timezone math doesn't push the
  // day across a boundary (new Date(iso) treats bare YYYY-MM-DD as UTC).
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  const then = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diffDays = Math.round((today.getTime() - then.getTime()) / 86400000);
  if (diffDays === 0)  return 'hoy';
  if (diffDays === 1)  return 'ayer';
  if (diffDays > 1 && diffDays <= 30) return `hace ${diffDays} días`;
  if (diffDays > 30 && diffDays < 365) {
    const months = Math.round(diffDays / 30);
    return months === 1 ? 'hace 1 mes' : `hace ${months} meses`;
  }
  if (diffDays >= 365) {
    const years = Math.round(diffDays / 365);
    return years === 1 ? 'hace 1 año' : `hace ${years} años`;
  }
  // Future date — flip the phrase.
  if (diffDays === -1) return 'mañana';
  if (diffDays < -1 && diffDays >= -30) return `en ${-diffDays} días`;
  return iso;
}

/**
 * Human-readable scope summary for a conteo físico session, e.g.
 * "Línea: GON–GP149 · clase A" or "Toda la tienda". Shared by the session
 * list and the session workspace header so the two surfaces don't drift.
 */
export function formatCountScope(
  s: { scope_type: string; scope_from: string; scope_to: string; class_filter: string },
): string {
  const labels = t.conteo.form.scopeOptions as Record<string, string>;
  const kind = labels[s.scope_type] ?? s.scope_type;
  let base: string;
  if (s.scope_type === 'all') {
    base = kind;
  } else if (s.scope_type === 'supplier') {
    base = `${kind}: ${s.scope_from || '—'}`;
  } else {
    const range = s.scope_to && s.scope_to !== s.scope_from ? `${s.scope_from}–${s.scope_to}` : s.scope_from;
    base = `${kind}: ${range || '—'}`;
  }
  if (s.class_filter && s.class_filter !== '*') base += ` · clase ${s.class_filter}`;
  return base;
}

/** Spanish label for a conteo físico session status; falls back to the raw code. */
export function countStatusLabel(status: string): string {
  return (t.conteo.status as Record<string, string>)[status] ?? status;
}

/** Badge color for a conteo físico session status. */
export function countStatusColor(status: string): 'blue' | 'amber' | 'emerald' | 'gray' {
  switch (status) {
    case 'contando':  return 'blue';
    case 'revision':  return 'amber';
    case 'posteado':  return 'emerald';
    case 'cancelado': return 'gray';
    default:          return 'gray';
  }
}

/**
 * Spanish label for a per-line post-count result status (postCountResult's
 * "posted" | "locked" | "skipped" | "error" — distinct from the session-level
 * status above). Falls back to the raw code.
 */
export function postLineStatusLabel(status: string): string {
  return (t.conteo.post.statuses as Record<string, string>)[status] ?? status;
}

/** Badge color for a per-line post-count result status. */
export function postLineStatusColor(status: string): 'blue' | 'amber' | 'emerald' | 'gray' | 'red' {
  switch (status) {
    case 'posted':  return 'emerald';
    case 'locked':  return 'amber';
    case 'skipped': return 'gray';
    case 'error':   return 'red';
    default:        return 'blue';
  }
}

export function rowsToCSV(rows: Record<string, unknown>[], cols: string[]): string {
  const escape = (v: unknown): string => {
    if (v == null) return '';
    let s: string;
    if (typeof v === 'object') s = JSON.stringify(v);
    else if (typeof v === 'number') s = Number.isInteger(v) ? String(v) : String(v);
    else s = String(v);
    if (/[",\n\r]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
    return s;
  };
  const lines: string[] = [];
  lines.push(cols.map(fieldLabel).map(escape).join(','));
  for (const row of rows) {
    lines.push(cols.map((c) => escape(row[c])).join(','));
  }
  // CRLF — Excel on Windows handles it more predictably than bare LF.
  return lines.join('\r\n') + '\r\n';
}

/** Spanish label for an order-reception session status; falls back to the raw code. */
export function receptionStatusLabel(status: string): string {
  return (t.recepcion.status as Record<string, string>)[status] ?? status;
}

/** Badge color for an order-reception session status. */
export function receptionStatusColor(status: string): 'blue' | 'amber' | 'emerald' | 'gray' {
  switch (status) {
    case 'abierto':    return 'gray';
    case 'escaneando': return 'blue';
    case 'revision':   return 'amber';
    case 'recibido':   return 'emerald';
    case 'cancelado':  return 'gray';
    default:           return 'gray';
  }
}

/**
 * Spanish label for a reconcile line's class ("exact"|"short"|"over" — a
 * ReconLine.class value) — or the synthetic "unexpected" used for off-PO
 * scans in the Unexpected section, which isn't itself a ReconLine.class
 * value. Falls back to the raw code.
 */
export function reconClassLabel(cls: string): string {
  return (t.recepcion.review.classes as Record<string, string>)[cls] ?? cls;
}

/** Badge color for a reconcile line's class (see reconClassLabel). */
export function reconClassColor(cls: string): 'blue' | 'amber' | 'emerald' | 'gray' | 'red' {
  switch (cls) {
    case 'exact':      return 'emerald';
    case 'short':      return 'red';
    case 'over':       return 'amber';
    case 'unexpected': return 'gray';
    default:           return 'blue';
  }
}

/**
 * Spanish label for a per-line post-receiving result status (PostResult's
 * "posted" | "locked" | "error" | "skipped" — distinct from the session-level
 * status above). Mirrors postLineStatusLabel's physcount equivalent. Falls
 * back to the raw code.
 */
export function postReceivingStatusLabel(status: string): string {
  return (t.recepcion.review.post.statuses as Record<string, string>)[status] ?? status;
}

/** Badge color for a per-line post-receiving result status. */
export function postReceivingStatusColor(status: string): 'blue' | 'amber' | 'emerald' | 'gray' | 'red' {
  switch (status) {
    case 'posted':  return 'emerald';
    case 'locked':  return 'amber';
    case 'skipped': return 'gray';
    case 'error':   return 'red';
    default:        return 'blue';
  }
}
